import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';
import { get } from 'lodash';
import { Trans } from 'react-i18next';
import ErrorIcon from '@material-ui/icons/Error';
import Cookies from 'js-cookie';
import { ContentWrapper, RouteContent } from './common/Sidemenu/style';
import localStore from '../utils/localStorage';
import { getQueryParams, isStaffPortal, replaceUrlParam } from '../utils';
import Header from './common/Header';
import Footer from './common/Footer';
import Sidemenu from './common/Sidemenu';
import SnackBar from './common/SnackBar';
import WithUser from './WithUser';
import AppTheme from './MainTheme';
import {
  OVERVIEW,
  PARENT_SEARCH,
  PARENT_INFO,
  HOMEPAGE,
  ENQUIRY,
  LOGIN,
  INTRO_PAGE,
  REGISTRATION_FORM_PREVIEW,
  ENRICHMENT_OVERVIEW,
  LP_SSO_CALLBACK,
  EP_LP_SSO_CALLBACK,
  EP_EXCHANGE_TOKEN,
} from '../routes/constants';
import protectedRoutes from '../routes/protected';
import publicRoutes from '../routes/public';
import parentRoutes from '../routes/protected/parent';
import staffRoutes from '../routes/protected/staff';
import {
  fetchUserDetails,
  hideSnackBarMessage,
  dispatchLogin,
  dispatchUserDetails,
} from '../redux/actions';
import { filterValidPathsForRoleBySideMenu } from '../utils/authUtil';
import { PRODUCTION, PROD } from '../utils/constants';

const NotFound = lazy(() => import('./common/NotFound'));
const ErrorBoundary = lazy(() => import('./common/ErrorBoundary'));

const App = ({
  history,
  location: { pathname, search },
  schoolID,
  userRole,
  snackBarMessage,
  hideSnackBarMessage,
  sidemenuData,
}) => {
  const isStaffDomain = isStaffPortal();

  const userToken = localStore.getValue('userToken');
  const isUserSessionActive = Boolean(userToken);
  const epCookie = Cookies.get('epUserToken');
  const epCookieRef = localStore.getValue('epCookieRef');

  const authUserRoutes = useMemo(() => {
    if (userRole && get(sidemenuData, 'length', 0)) {
      return filterValidPathsForRoleBySideMenu(
        userRole.isStaff ? staffRoutes : parentRoutes,
        sidemenuData,
        userRole.label
      );
    }
    return [];
  }, [userRole, sidemenuData]);

  let showLostError = false;

  const queryParams = getQueryParams();

  useEffect(() => {
    if (epCookie && epCookieRef !== epCookie) {
      const uriComponent = encodeURIComponent(`${pathname}${search}`);
      history.push(`${EP_EXCHANGE_TOKEN}?navigateTo=${uriComponent}`);
    }
  }, [epCookie]);

  useEffect(() => {
    const isApp = get(queryParams, 'isFromParentApp');
    if (typeof isApp !== 'undefined') {
      localStorage.setItem('isFromParentApp', isApp);
    }
  }, [get(queryParams, 'isFromParentApp')]);

  useEffect(() => {
    if (isStaffDomain) {
      window.fcWidget.hide();
    }
  }, [isStaffDomain]);

  const isFromParentApp = useMemo(
    () =>
      get(queryParams, 'isFromParentApp', false) ||
      localStorage.getItem('isFromParentApp') === 'true',
    [get(queryParams, 'isFromParentApp')]
  );

  if (
    pathname.indexOf(PARENT_SEARCH) === -1 &&
    pathname.indexOf(PARENT_INFO) === -1 &&
    pathname.indexOf(INTRO_PAGE) === -1 &&
    pathname.indexOf(ENQUIRY) === -1
  ) {
    let routeType = 0;
    if (pathname !== HOMEPAGE) {
      protectedRoutes.forEach(item => {
        if (item.url.indexOf(pathname.split('/')[1]) > -1) {
          showLostError = true;
          routeType = 1;
        }
      });
    }

    publicRoutes.forEach(item => {
      if (item.url.indexOf(pathname.split('/')[1]) > -1) {
        routeType = 2;
      }
    });

    if (routeType === 1 && !isUserSessionActive && !epCookie) {
      const uriComponent = encodeURIComponent(`${pathname}${search}`);
      history.push(`${LOGIN}?redirect=${uriComponent}`);
    } else if (routeType === 2 && isUserSessionActive) {
      if (
        !get(userRole, 'isStaff') &&
        process.env.REACT_APP_ENRICHMENT_PLATFORM === 'true'
      ) {
        if (
          !pathname.includes(EP_LP_SSO_CALLBACK) &&
          !pathname.includes(LP_SSO_CALLBACK) &&
          !(!epCookie || epCookie !== epCookieRef)
        ) {
          history.push(ENRICHMENT_OVERVIEW);
        }
      } else if (
        !pathname.includes(EP_LP_SSO_CALLBACK) &&
        !pathname.includes(LP_SSO_CALLBACK)
      ) {
        history.push(OVERVIEW);
      }
    } else if (routeType === 0 && isUserSessionActive) {
      showLostError = true;
    }
  }

  let showHeaderAndMenu = true;
  const urlArr = pathname.split('/');
  if (
    pathname ===
    replaceUrlParam(REGISTRATION_FORM_PREVIEW, {
      enquiryid: urlArr[urlArr.length - 1],
    })
  ) {
    showHeaderAndMenu = false;
  }

  if (userToken && window.location.search.includes('redirect')) {
    const uriComponent = decodeURIComponent(queryParams.redirect);
    history.push(uriComponent);
  }

  const fkSchool = schoolID;
  const bannerAlert = get(process.env, 'REACT_APP_ENVIRONMENT', 'production');

  return (
    <>
      {bannerAlert !== PRODUCTION && bannerAlert !== PROD && !isFromParentApp && (
        <div className="s7t-banner-alert w3-bar">
          <ErrorIcon className="s7t-error-icon" />
          <div className="sn2-banner-message">
            <Trans i18nKey="common.bannerAlertMsg" />
          </div>
        </div>
      )}

      <AppTheme fkSchool={fkSchool}>
        {get(snackBarMessage, 'visibility', false) && (
          <div
            className={
              get(snackBarMessage, 'snackBarInfo.anchor') === 'top'
                ? 'sn2-universal-alert'
                : 'sn2-universal-toast'
            }
          >
            <SnackBar
              open={get(snackBarMessage, 'visibility', false)}
              onClose={() => hideSnackBarMessage()}
              icon={get(snackBarMessage, 'snackBarInfo.icon')}
              message={get(snackBarMessage, 'snackBarInfo.message')}
              variant={get(snackBarMessage, 'snackBarInfo.variant', 'error')}
            />
          </div>
        )}
        {showLostError ? (
          <>
            <div
              className="s7t-sidemenu"
              style={{ display: showHeaderAndMenu ? '' : 'none' }}
            >
              {WithUser({ default: Sidemenu }, null, true)}
            </div>
            <ContentWrapper
              className={showHeaderAndMenu ? 's7t-logged-content' : ''}
            >
              <div
                style={{
                  display: isFromParentApp || !showHeaderAndMenu ? 'none' : '',
                }}
              >
                {WithUser({ default: Header })}
              </div>

              <RouteContent className="s7t-content">
                <Suspense fallback={<div />}>
                  <ErrorBoundary>
                    <Switch>
                      {authUserRoutes.map(eachItem => (
                        <Route
                          key={`rout-key-${eachItem.url}`}
                          path={eachItem.url}
                          exact
                          render={props =>
                            WithUser(eachItem.component, {
                              ...props,
                              fkSchool,
                            })
                          }
                        />
                      ))}
                      {authUserRoutes.length && (
                        <Route
                          render={props => (
                            <NotFound {...props} activeUserSession />
                          )}
                        />
                      )}
                    </Switch>
                  </ErrorBoundary>
                </Suspense>
              </RouteContent>
              <div
                style={{
                  display: isFromParentApp ? 'none' : '',
                }}
              >
                {WithUser({ default: Footer })}
              </div>
            </ContentWrapper>
          </>
        ) : (
          <Suspense fallback={<div />}>
            <ErrorBoundary>
              <Switch>
                {publicRoutes.map(eachRoute => (
                  <Route
                    key={eachRoute.url}
                    path={eachRoute.url}
                    exact
                    render={props => (
                      <eachRoute.component {...props} fkSchool={fkSchool} />
                    )}
                  />
                ))}
                <Route
                  render={props => (
                    <NotFound {...props} activeUserSession={false} />
                  )}
                />
              </Switch>
            </ErrorBoundary>
          </Suspense>
        )}
      </AppTheme>
    </>
  );
};

const mapStateToProps = state => ({
  schoolID: get(state, 'schoolID.data'),
  userRole: get(state, 'userDetails.data.userRoleRelations.data[0].role'),
  snackBarMessage: get(state, 'snackBarMessage.data'),
  sidemenuData: get(state, 'sidemenu.data'),
});

const mapDispatchToProps = {
  hideSnackBarMessage,
  fetchUserDetailsData: fetchUserDetails,
  dispatchUserDetails,
  dispatchLogin,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
